import { analytics } from '@tools/analytics.ts';
import type { EventProperties, TrackOptions as AnalyticsTrackOptions } from '@go-analytics/web-sdk';

export type AppArea = 'erp' | 'cms' | 'crm' | 'eshop_dashboard' | 'eshop_public';

export type BehaviorAnalyticsContext = {
  appArea: AppArea;
  feature: string;
  surface: string;
  entryPoint?: string;
  componentId?: string;
  flowId?: string;
  entityType?: string;
  entityId?: string | number;
};

export type BehaviorProperties = Record<string, unknown>;

export type ScheduleFunnelEvent =
  | 'schedule_discovery_opened'
  | 'schedule_filters_applied'
  | 'schedule_enrollment_review_opened'
  | 'schedule_enrollment_succeeded'
  | 'schedule_enrollment_failed';

export type BehaviorTrackOptions = Omit<AnalyticsTrackOptions, 'context'> & {
  context: BehaviorAnalyticsContext;
};

type BehaviorAnalyticsDebugSnapshot = {
  discardedNoClient: number;
  localErrors: number;
  eventCounts: Record<string, number>;
};

const debugState: BehaviorAnalyticsDebugSnapshot = {
  discardedNoClient: 0,
  localErrors: 0,
  eventCounts: {},
};

export const isBehaviorTrackingEnabled = (): boolean =>
  import.meta.env.VITE_SYSTEM_TRACKING_FRONTEND_ENABLED !== 'false';

export const getBehaviorAnalyticsDebugSnapshot = (): BehaviorAnalyticsDebugSnapshot => ({
  discardedNoClient: debugState.discardedNoClient,
  localErrors: debugState.localErrors,
  eventCounts: { ...debugState.eventCounts },
});

export const resetBehaviorAnalyticsDebugSnapshot = (): void => {
  debugState.discardedNoClient = 0;
  debugState.localErrors = 0;
  debugState.eventCounts = {};
};

const BLOCKED_KEYS = new Set([
  'password',
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'cookie',
  'secret',
  'private_key',
  'credit_card',
  'card_number',
  'cvv',
  'dni',
  'document',
  'email',
  'phone',
  'address',
]);

const MAX_DEPTH = 8;

export const toAnalyticsContext = (context: BehaviorAnalyticsContext): EventProperties => {
  const serialized: EventProperties = {
    app_area: context.appArea,
    feature: context.feature,
    surface: context.surface,
  };
  addOptionalString(serialized, 'entry_point', context.entryPoint);
  addOptionalString(serialized, 'component_id', context.componentId);
  addOptionalString(serialized, 'flow_id', context.flowId);
  addOptionalString(serialized, 'entity_type', context.entityType);
  if (context.entityId !== undefined && context.entityId !== null) {
    serialized.entity_id = String(context.entityId);
  }
  return serialized;
};

export const sanitizeAnalyticsPayload = (payload: BehaviorProperties = {}): EventProperties => {
  const sanitized = sanitizeValue(payload, 0);
  return isPlainObject(sanitized) ? sanitized as EventProperties : {};
};

export const trackBehaviorEvent = (
  eventName: string,
  properties: BehaviorProperties,
  options: BehaviorTrackOptions,
): void => {
  if (!isBehaviorTrackingEnabled()) {
    return;
  }
  try {
    if (!analytics?.track) {
      debugState.discardedNoClient += 1;
      return;
    }
    debugState.eventCounts[eventName] = (debugState.eventCounts[eventName] ?? 0) + 1;
    analytics?.track(eventName, sanitizeAnalyticsPayload(properties), {
      ...options,
      context: toAnalyticsContext(options.context),
    });
  } catch {
    debugState.localErrors += 1;
    // Tracking must never break the host UI.
  }
};

export const trackScheduleFunnelEvent = (
  eventName: ScheduleFunnelEvent,
  properties: BehaviorProperties = {},
): void => {
  trackBehaviorEvent(eventName, properties, {
    context: {
      appArea: 'erp',
      feature: 'account_schedule',
      surface: 'self_service',
      entryPoint: 'account_turns',
    },
  });
};

export const trackFeatureOpened = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties = {},
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('feature_opened', properties, {
    ...options,
    context,
    logicalEventId: options.logicalEventId ?? logicalId('feature_opened', context),
  });
};

export const trackFeatureAction = (
  context: BehaviorAnalyticsContext,
  action: string,
  properties: BehaviorProperties = {},
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('feature_action_performed', { ...properties, action }, { ...options, context });
};

export const trackSearchPerformed = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('search_performed', properties, { ...options, context });
};

export const trackSearchResultSelected = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('search_result_selected', properties, { ...options, context });
};

export const trackSearchEmptyResult = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('search_empty_result', properties, { ...options, context });
};

export const trackSearchAbandoned = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties = {},
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('search_abandoned', properties, { ...options, context });
};

export const trackFlowAbandoned = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties = {},
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('flow_abandoned', properties, { ...options, context });
};

export const trackItemSelectedForContext = (
  context: BehaviorAnalyticsContext,
  properties: {
    item_id: string | number;
    variant_id?: string | number | null;
    item_type?: string | null;
    search_id?: string;
    position?: number;
    source?: string;
  },
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('item_selected_for_context', properties, {
    ...options,
    context,
    logicalEventId: options.logicalEventId ?? logicalId('item_selected_for_context', context, String(properties.item_id)),
  });
};

export const trackFormValidationAttempt = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('form_validation_attempt', properties, { ...options, context, critical: options.critical ?? true });
};

export const trackFormStepViewed = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('form_step_viewed', properties, { ...options, context });
};

export const trackFormStepAdvanced = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('form_step_advanced', properties, { ...options, context });
};

export const trackFormCompleted = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('form_completed', properties, { ...options, context, critical: options.critical ?? true });
};

export const trackFormAbandoned = (
  context: BehaviorAnalyticsContext,
  properties: BehaviorProperties,
  options: Omit<BehaviorTrackOptions, 'context'> = {},
): void => {
  trackBehaviorEvent('form_abandoned', properties, { ...options, context });
};

export const routeContextForPath = (path: string): BehaviorAnalyticsContext | null => {
  const normalized = path.toLowerCase();
  if (normalized.startsWith('/erp/appointments') || normalized.startsWith('/app/erp/appointments')) {
    return { appArea: 'erp', feature: 'appointments', surface: 'fullscreen', entryPoint: 'route' };
  }
  if (normalized.startsWith('/erp/pos') || normalized.startsWith('/app/erp/pos')) {
    return { appArea: 'erp', feature: 'pos', surface: 'fullscreen', entryPoint: 'route' };
  }
  if (normalized.startsWith('/erp/subscriptions') || normalized.startsWith('/app/erp/subscriptions')) {
    return { appArea: 'erp', feature: 'subscriptions', surface: 'fullscreen', entryPoint: 'route' };
  }
  if (normalized.startsWith('/crm') || normalized.startsWith('/app/crm')) {
    return { appArea: 'crm', feature: 'customers', surface: 'fullscreen', entryPoint: 'route' };
  }
  if (normalized.startsWith('/cms') || normalized.startsWith('/app/cms')) {
    return { appArea: 'cms', feature: 'cms_pages', surface: 'fullscreen', entryPoint: 'route' };
  }
  if (normalized.startsWith('/dashboard') || normalized.startsWith('/app/dashboard')) {
    return { appArea: 'eshop_dashboard', feature: 'catalog', surface: 'fullscreen', entryPoint: 'route' };
  }
  if (normalized.startsWith('/eshop') || normalized.startsWith('/catalogue') || normalized.startsWith('/checkout')) {
    return { appArea: 'eshop_public', feature: 'catalog', surface: 'fullscreen', entryPoint: 'route' };
  }
  return null;
};

export const trackPageView = (path: string, title: string): void => {
  if (!isBehaviorTrackingEnabled()) {
    return;
  }
  const context = routeContextForPath(path);
  try {
    if (!analytics?.page) {
      debugState.discardedNoClient += 1;
      return;
    }
    debugState.eventCounts.page_view = (debugState.eventCounts.page_view ?? 0) + 1;
    analytics.page({}, {
      path,
      title,
      ...(context ? { context: toAnalyticsContext(context) } : {}),
    });
  } catch {
    debugState.localErrors += 1;
    // Tracking must never break navigation.
  }
};

const logicalId = (eventName: string, context: BehaviorAnalyticsContext, suffix = ''): string => {
  const parts = [
    eventName,
    context.appArea,
    context.feature,
    context.surface,
    context.entryPoint ?? 'none',
    context.componentId ?? 'none',
    context.flowId ?? 'none',
    suffix,
  ];
  return parts.map((part) => encodeURIComponent(part)).join(':');
};

const addOptionalString = (target: EventProperties, key: string, value: string | undefined): void => {
  if (value && value.trim()) {
    target[key] = value.trim();
  }
};

const sanitizeValue = (value: unknown, depth: number): unknown => {
  if (depth > MAX_DEPTH) {
    return undefined;
  }
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeValue(item, depth + 1))
      .filter((item): item is NonNullable<unknown> => item !== undefined);
  }
  if (!isPlainObject(value)) {
    return undefined;
  }
  const sanitized: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (BLOCKED_KEYS.has(key.trim().toLowerCase())) {
      continue;
    }
    const nextValue = sanitizeValue(nested, depth + 1);
    if (nextValue !== undefined) {
      sanitized[key] = nextValue;
    }
  }
  return sanitized;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
